/**
 * propertiesStore
 *
 * Loads `/svf/rac-basic/properties.json` once at boot and exposes a
 * dbId -> ElementProperties lookup. This sidecar is generated by
 * `apps/web/scripts/extract-properties.cjs` from `Resource/model.sdb`.
 *
 * Why we don't use viewer.getProperties()
 * ---------------------------------------
 * The pre-extracted Revit SVF bundle's property data lives only in
 * `Resource/`, not co-located with each f2d as Forge Viewer v7 expects.
 * Pre-extracting to JSON sidesteps the load-order race that returns
 * `{instanceTree: null}` and matches Phase 3's intended architecture
 * (properties come from AEC DM, not from the viewer).
 */
import { create } from "zustand";

export interface ElementProperty {
  displayName: string;
  displayCategory: string;
  displayValue: string;
}

export interface ElementProperties {
  dbId: number;
  name?: string;
  category?: string;
  properties: ElementProperty[];
}

interface PropertiesFile {
  generatedAt: string;
  modelRef: string;
  source: string;
  dbIdCount: number;
  elements: Record<string, ElementProperties>;
}

interface PropertiesStore {
  byDbId: Map<number, ElementProperties>;
  loaded: boolean;
  loadError: string | null;
  loadFromUrl: (url: string) => Promise<void>;
  loadFromMap: (map: Map<number, ElementProperties>) => void;
  get: (dbId: number) => ElementProperties | undefined;
}

export const usePropertiesStore = create<PropertiesStore>((set, get) => ({
  byDbId: new Map(),
  loaded: false,
  loadError: null,

  loadFromUrl: async url => {
    // No early-return on `loaded`: the project switcher needs to
    // re-call this with a new URL when the user changes projects.
    // Reset state up-front so the UI shows "Loading..." instead of
    // stale data from the previous project while the fetch is in
    // flight.
    set({ byDbId: new Map(), loaded: false, loadError: null });
    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status} loading ${url}`);
      }
      const data = (await res.json()) as PropertiesFile;
      const map = new Map<number, ElementProperties>();
      for (const [k, v] of Object.entries(data.elements)) {
        map.set(Number(k), v);
      }
      set({ byDbId: map, loaded: true, loadError: null });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[Vera] Failed to load properties.json:", msg);
      set({ loaded: true, loadError: msg });
    }
  },

  loadFromMap: map => {
    set({ byDbId: new Map(map), loaded: true, loadError: null });
  },

  get: dbId => get().byDbId.get(dbId),
}));
